import {
  combineReducers,
  configureStore,
  Middleware,
  PreloadedState,
} from '@reduxjs/toolkit';
import { modalReducer } from './modalSlice';
import { gameReducer } from './gameSlice';
import { multiplayerReducer } from './multiplayerSlice';
import { profileReducer } from './profileSlice';

const rootReducer = combineReducers({
  modal: modalReducer,
  game: gameReducer,
  multiplayer: multiplayerReducer,
  profile: profileReducer,
});

export function setupStore(preloadedState?: PreloadedState<RootState>) {
  return configureStore({
    reducer: rootReducer,
    preloadedState,
  });
}

//MIDDLEWARE
const STORAGE_KEY = 'connectFourGameSettigns';

const localStorageMiddleware: Middleware = ({ getState }) => {
  return (next) => (action) => {
    const result = next(action);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(getState()));
    } catch {
      // Quota or privacy-mode errors are non-fatal.
    }
    return result;
  };
};

/**
 * Rehydrate from localStorage but clear transient socket-lifecycle flags.
 * These flags describe "live" connection state that is necessarily false
 * on a fresh page load — persisting them causes misleading UI (e.g. showing
 * "connected" while the socket is actually still reconnecting, or showing
 * stale error banners from a previous session).
 *
 * `roomId`, `role`, `myColor`, and `room` ARE kept so that the game page can
 * auto-rejoin the previous match.
 */
const reHydrateStore = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.multiplayer) {
      parsed.multiplayer = {
        ...parsed.multiplayer,
        isConnected: false,
        isConnecting: false,
        error: null,
      };
    }
    return parsed;
  } catch {
    // Corrupted state — drop it and start fresh.
    localStorage.removeItem(STORAGE_KEY);
    return undefined;
  }
};

export const store = configureStore({
  reducer: rootReducer,
  preloadedState: reHydrateStore(),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(localStorageMiddleware),
});
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
